import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { buildReferencePlaceTooltipHtml } from '../utils/referencePlaceTooltip';
import { referencePlaceKindLabel } from '../data/referencePlaceKinds';
import { queryReferencePlaceAtPoint } from '../utils/referencePlaces';

/**
 * Hover de pines (tiles o GeoJSON). Al salir se oculta; el click del lote
 * debajo sigue andando.
 */
export function useReferencePlacesHover({
  mapInstanceRef,
  enabled,
  kindLabels = {},
}) {
  const popupRef = useRef(null);
  const kindLabelsRef = useRef(kindLabels);
  kindLabelsRef.current = kindLabels;

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !enabled) {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      return undefined;
    }

    let hoveringPin = false;
    const canvas = map.getCanvas();

    const hidePopup = () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    };

    const leavePin = () => {
      hidePopup();
      if (hoveringPin) {
        canvas.style.cursor = '';
        hoveringPin = false;
      }
    };

    const ensurePopup = () => {
      if (popupRef.current) return popupRef.current;
      popupRef.current = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 14,
        className: 'terrio-place-tooltip',
        maxWidth: '259px',
      });
      return popupRef.current;
    };

    const handleMapMouseMove = (event) => {
      const feature = queryReferencePlaceAtPoint(map, event.point);
      if (!feature) {
        leavePin();
        return;
      }
      const properties = feature.properties || {};
      const kind = String(properties.kind || '').trim();
      const html = buildReferencePlaceTooltipHtml({
        ...properties,
        kindLabel: kindLabelsRef.current[kind]
          || properties.kindLabel
          || referencePlaceKindLabel(kind)
          || kind,
      });
      hoveringPin = true;
      if (!html) {
        hidePopup();
        return;
      }
      ensurePopup().setLngLat(event.lngLat).setHTML(html).addTo(map);
      canvas.style.cursor = 'pointer';
    };

    map.on('mousemove', handleMapMouseMove);
    canvas.addEventListener('mouseleave', leavePin);

    return () => {
      leavePin();
      try { map.off('mousemove', handleMapMouseMove); } catch (_) { /* noop */ }
      canvas.removeEventListener('mouseleave', leavePin);
    };
  }, [enabled, mapInstanceRef]);
}
